import { FileCode, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import type { TraceNode } from '@/lib/utils/traceParser'

interface TraceDetailProps {
    node: TraceNode | null
}

function DetailBlock({ title, content }: { title: string, content: any }) {
    if (!content) return null
    if (typeof content === 'object' && Object.keys(content).length === 0) return null

    return (
        <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">{title}</span>
            <div className="p-3 bg-black/50 border border-white/5 rounded-lg overflow-auto max-h-60 custom-scrollbar group relative">
                <button
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(content, null, 2))}
                    className="absolute top-2 right-2 p-1 text-xs bg-white/10 hover:bg-white/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Copy JSON"
                >
                    Copy
                </button>
                <pre className={`text-[10px] font-mono whitespace-pre-wrap ${title === 'Outputs' ? 'text-green-400' :
                    title === 'Error' ? 'text-red-400' : 'text-gray-300'
                    }`}>
                    {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
                </pre>
            </div>
        </div>
    )
}

export function TraceDetail({ node }: TraceDetailProps) {
    if (!node) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-600 italic h-full p-8">
                <FileCode className="w-12 h-12 opacity-20 mb-3" />
                <p className="text-center text-sm">Select a step in the execution tree<br />to view inputs, outputs and metadata.</p>
            </div>
        )
    }

    return (
        <div className="h-full flex flex-col overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Header */}
            <div className="pb-4 border-b border-white/10 shrink-0">
                <h3 className="text-lg font-medium text-blue-400 truncate" title={node.name}>{node.name}</h3>
                <div className="flex items-start gap-2 mt-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-white/10 rounded text-[10px] text-gray-400 uppercase tracking-wider font-mono">
                        {node.type}
                    </span>
                    <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono ${node.status === 'success' ? 'bg-green-500/10 text-green-400' :
                        node.status === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-500'
                        }`}>
                        {node.status === 'success' && <CheckCircle2 className="w-3 h-3" />}
                        {node.status === 'error' && <XCircle className="w-3 h-3" />}
                        {node.status === 'warning' && <AlertTriangle className="w-3 h-3" />}
                        {node.status}
                    </span>
                    {node.latency && (
                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 rounded text-[10px] font-mono">
                            {node.latency.toFixed(0)}ms
                        </span>
                    )}
                </div>
                {node.error && (
                    <div className="mt-3 p-2 bg-red-900/20 border border-red-500/30 rounded text-xs text-red-300 break-words font-mono">
                        {node.error}
                    </div>
                )}
            </div>

            {/* Content Scrollable Area */}
            <div className="flex-1 overflow-auto custom-scrollbar space-y-6 pt-6">
                <DetailBlock title="Inputs" content={node.inputs} />
                <DetailBlock title="Outputs" content={node.outputs} />

                {/* Metrics / Metadata - Conditional Display */}
                {node.metadata && (
                    <>
                        {/* Special handling for known metric keys if needed, otherwise generic dump */}
                        <DetailBlock title="Metadata & Metrics" content={node.metadata} />
                    </>
                )}
            </div>
        </div>
    )
}
